// @ts-check
// Dados de apoio das telas dos fluxos (contrato 09 de fluxos, seção 17): modelos de e-mail e de WhatsApp, números
// conectados, membros e os outros fluxos, carregados uma vez por tela. Com eles saem os nomes do resumo de cada passo
// (shared/flows/flow-summary.js) e o contexto da validação da tela (client/src/lib/flow-validation.js). Falha ao
// carregar um deles não derruba a tela: o resumo cai no texto genérico e a regra daquele dado fica para o servidor.
import { useEffect, useMemo, useState } from 'preact/hooks';
import { listEmailTemplates } from '../../data/email-templates.js';
import { listWhatsappTemplates } from '../../data/integrations.js';
import { listChannels } from '../../data/conversations.js';
import { listFlows } from '../../data/flows.js';
import { loadWorkspaceMembers, workspaceMembersState } from '../../data/workspace-members.js';
import { fieldsById, hasCurrentPermissionFlag, objectsByNameSingular, viewsState } from '../../data/metadata-store.js';
import { summarizeAudience } from '../../lib/audience-summary.js';
import { normalizeAudienceDefinition } from '../../../../shared/audiences/audience-definition.js';
import { isTemplateSendable } from '../../../../shared/whatsapp/template-rules.js';
import { FLOW_TIME_ZONE } from '../../../../shared/flows/flow-wait-time.js';
import { indexFlowNodes } from '../../../../shared/flows/flow-definition.js';
import { translateMetadataLabel } from '../../i18n/index.js';

/**
 * @typedef {import('../../data/email-templates.js').EmailTemplateSummary} EmailTemplateSummary
 * @typedef {import('../../data/integrations.js').WhatsappTemplate} WhatsappTemplate
 * @typedef {import('../../data/conversations.js').Channel} Channel
 * @typedef {import('../../lib/flow-list.js').FlowSummary} FlowSummary
 * @typedef {import('../../../../shared/flows/flow-definition.js').FlowDefinition} FlowDefinition
 * @typedef {import('../../../../shared/flows/flow-summary.js').FlowSummaryLookups} FlowSummaryLookups
 * @typedef {{ emailTemplates: EmailTemplateSummary[] | null, whatsappTemplates: WhatsappTemplate[] | null, channels: Channel[] | null,
 *   flows: FlowSummary[] | null }} FlowLookupData
 */

export const CARD_OBJECT_NAME = 'enrollmentOpportunity';

/** Objeto do card (o público dos fluxos). */
export const cardObject = () => objectsByNameSingular.value.get(CARD_OBJECT_NAME);

/** Campo do card pelo nome. @param {string} fieldName */
export const cardField = (fieldName) => cardObject()?.fields?.find((field) => field.name === fieldName) ?? null;

/** Opções da etapa do funil, com rótulo e cor. */
export function stageOptions() {
  return /** @type {Array<{ value: string, label: string, color?: string | null }>} */ ((cardField('stage')?.options ?? [])
    .map((option) => ({ value: option.value, label: translateMetadataLabel(option.label), color: option.color ?? null })));
}

/** Nome de um membro da equipe. @param {{ name?: { firstName?: string, lastName?: string }, userEmail?: string } | null | undefined} member */
export const memberDisplayName = (member) => (member ? [member.name?.firstName, member.name?.lastName].filter(Boolean).join(' ') || member.userEmail || '' : '');

const dateFormatter = new Intl.DateTimeFormat('pt-BR', { timeZone: FLOW_TIME_ZONE, day: '2-digit', month: '2-digit', year: 'numeric' });
const timeFormatter = new Intl.DateTimeFormat('pt-BR', { timeZone: FLOW_TIME_ZONE, hour: '2-digit', minute: '2-digit', hour12: false });

/** Data e hora no horário de Brasília (as esperas do fluxo usam esse fuso). @param {string} instant */
export function brasiliaDateTime(instant) {
  const date = new Date(instant);
  if (Number.isNaN(date.getTime())) return null;
  return { date: dateFormatter.format(date), time: timeFormatter.format(date) };
}

/**
 * Carrega os dados de apoio (cada um por conta própria).
 * @param {{ includeFlows?: boolean }} [options]
 * @returns {FlowLookupData & { reload: () => void }}
 */
export function useFlowLookupData(options = {}) {
  const [data, setData] = useState(/** @type {FlowLookupData} */ ({ emailTemplates: null, whatsappTemplates: null, channels: null, flows: null }));
  const [version, setVersion] = useState(0);
  useEffect(() => {
    let isCancelled = false;
    const merge = (/** @type {Partial<FlowLookupData>} */ patch) => {
      if (!isCancelled) setData((current) => ({ ...current, ...patch }));
    };
    loadWorkspaceMembers();
    listEmailTemplates().then((emailTemplates) => merge({ emailTemplates })).catch(() => merge({ emailTemplates: [] }));
    listWhatsappTemplates({}).then((whatsappTemplates) => merge({ whatsappTemplates })).catch(() => merge({ whatsappTemplates: [] }));
    listChannels().then((result) => merge({ channels: result.channels.filter((channel) => channel.type === 'WHATSAPP') })).catch(() => merge({ channels: [] }));
    if (options.includeFlows !== false) listFlows().then((result) => merge({ flows: result.flows })).catch(() => merge({ flows: [] }));
    return () => {
      isCancelled = true;
    };
  }, [version]);
  return { ...data, reload: () => setVersion((value) => value + 1) };
}

/**
 * Consultas do resumo de cada passo (nomes de modelo, etapa, campo, membro, fluxo e público).
 * @param {FlowLookupData} data @param {FlowDefinition | null} definition
 * @returns {FlowSummaryLookups}
 */
export function buildSummaryLookups(data, definition) {
  const emailById = new Map((data.emailTemplates ?? []).map((template) => [template.id, template]));
  const whatsappById = new Map((data.whatsappTemplates ?? []).map((template) => [template.id, template]));
  const flowById = new Map((data.flows ?? []).map((flow) => [flow.id, flow]));
  const members = workspaceMembersState.value ?? [];
  const index = definition ? indexFlowNodes(definition) : new Map();
  const object = cardObject();
  return {
    emailTemplate: (id) => {
      const template = emailById.get(id);
      return template ? { name: template.name, purpose: template.purpose } : null;
    },
    whatsappTemplate: (id) => {
      const template = whatsappById.get(id);
      return template ? { name: template.name, category: template.category } : null;
    },
    stageLabel: (value) => stageOptions().find((option) => option.value === value)?.label ?? null,
    optionLabel: (fieldName, value) => {
      const option = object?.fields?.find((field) => field.name === fieldName)?.options?.find((candidate) => candidate.value === value);
      return option ? translateMetadataLabel(option.label) : null;
    },
    fieldLabel: (fieldName) => {
      const field = object?.fields?.find((candidate) => candidate.name === fieldName);
      return field ? translateMetadataLabel(field.label) : null;
    },
    memberName: (id) => memberDisplayName(members.find((member) => member.id === id)) || null,
    flowName: (id) => flowById.get(id)?.name ?? null,
    emailNodeLabel: (nodeId) => {
      const node = index.get(nodeId)?.node;
      if (!node) return null;
      if (node.name) return node.name;
      return node.config?.templateId ? emailById.get(node.config.templateId)?.name ?? null : null;
    },
    audiencePhrases: (audience) => {
      const usage = audience?.presets?.lost === 'INCLUDE' ? 'CONDITION' : 'AUDIENCE';
      const normalized = normalizeAudienceDefinition(audience, { usage }).definition;
      return summarizeAudience(normalized, { getField: (id) => fieldsById.value.get(id), views: viewsState.value, usage });
    },
    formatDateTime: brasiliaDateTime,
  };
}

/**
 * Contexto da validação da tela com o que já está carregado.
 * @param {FlowLookupData} data @param {{ flowId: string, activating?: boolean }} options
 * @returns {import('../../lib/flow-validation.js').FlowValidationContext}
 */
export function buildValidationContext(data, options) {
  const object = cardObject();
  return {
    fields: object ? new Map(object.fields.map((field) => [field.name, /** @type {any} */ (field)])) : null,
    stageValues: object ? new Set(stageOptions().map((option) => option.value)) : null,
    memberIds: workspaceMembersState.value ? new Set(workspaceMembersState.value.map((member) => member.id)) : null,
    flows: data.flows ? new Map(data.flows.map((flow) => [flow.id, { status: flow.status }])) : null,
    emailTemplates: data.emailTemplates ? new Map(data.emailTemplates.map((template) => [template.id, template])) : null,
    whatsappTemplates: data.whatsappTemplates
      ? new Map(data.whatsappTemplates.map((template) => [template.id, { ...template, isSendable: isTemplateSendable(/** @type {any} */ (template)) }]))
      : null,
    channelIds: data.channels ? new Set(data.channels.map((channel) => channel.id)) : null,
    canUseWhatsappMarketing: hasCurrentPermissionFlag('WHATSAPP_CAMPAIGNS'),
    flowId: options.flowId,
    now: new Date().toISOString(),
    activating: options.activating ?? false,
  };
}

/**
 * Memoriza as consultas do resumo enquanto os dados e a definição não mudam.
 * @param {FlowLookupData} data @param {FlowDefinition | null} definition
 */
export function useSummaryLookups(data, definition) {
  const members = workspaceMembersState.value;
  return useMemo(() => buildSummaryLookups(data, definition), [data.emailTemplates, data.whatsappTemplates, data.flows, members, definition]);
}
